import axios from './axiosInstance';

const requirePositiveId = (value, label) => {
  const id = Number(value);
  if (!Number.isInteger(id) || id <= 0) {
    throw new Error(`${label} is required before requesting boiler data.`);
  }
  return id;
};

const api = {
  listBoilers: (locationId) => {
    const id = requirePositiveId(locationId, 'Location ID');
    return axios.get(`/api/boilers/location/${id}`).then((r) => r.data);
  },
  createBoiler: (locationId, payload) => {
    const id = requirePositiveId(locationId, 'Location ID');
    return axios.post(`/api/boilers/location/${id}`, payload).then((r) => r.data);
  },
  getMatrix: (boilerId) => axios.get(`/api/boilers/${boilerId}/matrix`).then((r) => r.data),
  getMatrixManagement: (boilerId) => axios.get(`/api/boilers/${boilerId}/matrix/management`).then((r) => r.data),
  createMatrixLine: (boilerId, payload) => axios.post(`/api/boilers/${boilerId}/matrix-lines`, payload),
  updateMatrixLineDefinition: (lineId, payload) => axios.put(`/api/boilers/matrix-lines/${lineId}/definition`, payload),
  archiveMatrixLine: (lineId) => axios.post(`/api/boilers/matrix-lines/${lineId}/archive`),
  restoreMatrixLine: (lineId) => axios.post(`/api/boilers/matrix-lines/${lineId}/restore`),
  reorderMatrixLines: (boilerId, matrixLineIds) => axios.put(`/api/boilers/${boilerId}/matrix-lines/order`, { matrixLineIds }),
  copyMatrixLines: (boilerId, sourceBoilerId, matrixLineIds) => axios.post(`/api/boilers/${boilerId}/matrix-lines/copy`, { sourceBoilerId, matrixLineIds }),
  updateMatrixLine: (lineId, payload) => axios.put(`/api/boilers/matrix-lines/${lineId}`, payload).then((r) => r.data),
  listReports: (boilerId) => axios.get(`/api/boilers/${boilerId}/inspection-reports`).then((r) => r.data),
  listEvidence: (boilerId) => axios.get(`/api/boilers/${boilerId}/evidence`).then((r) => r.data),
  listDocuments: (boilerId) => axios.get(`/api/boilers/${boilerId}/documents`).then((r) => r.data),
  createEvidence: (boilerId, payload) => axios.post(`/api/boilers/${boilerId}/evidence`, payload).then((r) => r.data),
  updateEvidence: (evidenceId, payload) => axios.put(`/api/boilers/evidence/${evidenceId}`, payload).then((r) => r.data),
  linkEvidence: (evidenceId, matrixLineIds) => axios.post(`/api/boilers/evidence/${evidenceId}/links`, { matrixLineIds }).then((r) => r.data),
  cloneEvidence: (evidenceId, matrixLineId) => axios.post(`/api/boilers/evidence/${evidenceId}/clone`, null, { params: { matrixLineId } }).then((r) => r.data),
  unlinkEvidence: (lineId, evidenceId) => axios.delete(`/api/boilers/matrix-lines/${lineId}/evidence/${evidenceId}`),
  archiveEvidence: (evidenceId) => axios.post(`/api/boilers/evidence/${evidenceId}/archive`).then((r) => r.data),
  deleteDocument: (documentId) => axios.delete(`/api/boilers/documents/${documentId}`),
};

export async function uploadReport(boilerId, values) {
  const form = new FormData();
  form.append('file', values.file);
  form.append('title', values.title);
  form.append('reportType', values.reportType);
  form.append('inspectionDate', values.inspectionDate);
  form.append('currentReport', String(values.currentReport));
  return axios.post(`/api/boilers/${boilerId}/inspection-reports`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  }).then((r) => r.data);
}

export async function uploadBoilerDocument(boilerId, category, displayName, file) {
  const form = new FormData();
  form.append('file', file);
  if (displayName) form.append('displayName', displayName);
  return axios.post(`/api/boilers/${boilerId}/documents/${category}`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  }).then((r) => r.data);
}

export async function fetchBlobUrl(path, params = {}) {
  const response = await axios.get(path, { params, responseType: 'blob', timeout: 60000 });
  return window.URL.createObjectURL(new Blob([response.data], {
    type: response.headers['content-type'] || 'application/octet-stream',
  }));
}

export async function openContent(path) {
  const previewWindow = window.open('', '_blank');
  if (!previewWindow) {
    window.alert('Your browser blocked the preview window. Allow pop-ups for ODIN and try again.');
    return;
  }
  previewWindow.opener = null;

  try {
    const url = await fetchBlobUrl(path, { download: false });
    previewWindow.location.replace(url);
    window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  } catch (error) {
    previewWindow.close();
    console.error('Unable to preview boiler content:', error);
    window.alert('Unable to open this preview. Please try again.');
  }
}

export async function downloadContent(path, filename) {
  try {
    const url = await fetchBlobUrl(path, { download: true });
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'download';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Unable to download boiler content:', error);
    window.alert('Unable to download this file. Please try again.');
  }
}

export default api;
